import React, { useState } from "react";
import axios from "axios";
import Toast from "./Toast";

const CreateSilatItemModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    requiredQuantity: "",
    unit: "",
    price: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [fieldErrors, setFieldErrors] = useState({});

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" })); // clear error on change
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.unit.trim()) errors.unit = "Unit is required";
    if (!formData.requiredQuantity || parseFloat(formData.requiredQuantity) <= 0)
      errors.requiredQuantity = "Required quantity must be greater than 0";
    if (!formData.price || parseFloat(formData.price) <= 0)
      errors.price = "Price must be greater than 0";

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    if (!validateForm()) {
      setToast({ message: "Please fix the errors before submitting", type: "error" });
      setSubmitting(false);
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URI}/api/silatItem/create`,
        {
          name: formData.name,
          requiredQuantity: parseFloat(formData.requiredQuantity),
          unit: formData.unit,
          price: parseFloat(formData.price),
        },
        { withCredentials: true }
      );

      setToast({ message: "Silat Item created successfully!", type: "success" });
      setFormData({
        name: "",
        requiredQuantity: "",
        unit: "",
        price: "",
      });
    } catch (err) {
        console.log(err);
        
      setToast({
        message: err.response?.data?.custommessage || "Something went wrong",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-fmb text-white p-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Create Silat Item</h2>
            <button
              onClick={() => {
                onClose();
                setFormData({
                  name: "",
                  requiredQuantity: "",
                  unit: "",
                  price: "",
                });
             }}
              className="text-white text-2xl font-bold leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Item Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg"
              placeholder="Enter item name"
              required
            />
            {fieldErrors.name && (
              <p className="text-red-500 text-sm">{fieldErrors.name}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Required Quantity
              </label>
              <input
                type="number"
                name="requiredQuantity"
                value={formData.requiredQuantity}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg"
                placeholder="Enter required quantity"
                min="0.01"
                step="0.01"
                required
              />
              {fieldErrors.requiredQuantity && (
                <p className="text-red-500 text-sm">{fieldErrors.requiredQuantity}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit
              </label>
              <input
                type="text"
                name="unit"
                value={formData.unit}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg"
                placeholder="e.g. kg, liters, pcs"
                required
              />
              {fieldErrors.unit && (
                <p className="text-red-500 text-sm">{fieldErrors.unit}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price (₹)
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg"
              placeholder="Enter price per unit"
              min="0.01"
              step="0.01"
              required
            />
            {fieldErrors.price && (
              <p className="text-red-500 text-sm">{fieldErrors.price}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={() => {
                 onClose();
                 setFormData({
                   name: "",
                   requiredQuantity: "",
                   unit: "",
                   price: "",
                 });
              }}
              className="px-6 py-2 border border-gray-300 text-gray-600 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-green-600 text-white rounded-lg"
              disabled={submitting}
            >
              {submitting ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </div>

      {toast.message && (
        <Toast
            message={toast.message}
            type={toast.type}
            duration={3000}
            onClose={() => {
              setToast({ message: "", type: "success" });
              setFormData({
                name: "",
                requiredQuantity: "",
                unit: "",
                price: "",
              });
              onClose();
            }}
          />
        )}

    </div>
  );
};

export default CreateSilatItemModal;
